import React, { useState } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../../../../context/DarkModeContext';
import { CareerFocusWithMetrics } from '../../../../hooks/useCareerFocus';
import { ReviewPrepMetrics } from '../../../../hooks/useInternshipReviewPrep';
import { XMarkIcon } from '@heroicons/react/24/outline';
import EvidenceReviewStep from './EvidenceReviewStep';
import ReflectionStep, { ReflectionData } from './ReflectionStep';
import TalkingPointsStep, { TalkingPointsData } from './TalkingPointsStep';
import { TalkingPointsResponse } from '../../../../hooks/useTalkingPoints';

interface PreparationWizardProps {
  focus: CareerFocusWithMetrics;
  metrics: ReviewPrepMetrics;
  onClose: () => void;
  onComplete: (data: { reflections: ReflectionData; talkingPoints: TalkingPointsData; aiTalkingPoints?: Record<string, TalkingPointsResponse> }) => void;
  existingData?: {
    reflections: ReflectionData;
    talkingPoints: TalkingPointsData;
    aiTalkingPoints?: Record<string, TalkingPointsResponse>;
  };
}

const PreparationWizard: React.FC<PreparationWizardProps> = ({
  focus,
  metrics,
  onClose,
  onComplete,
  existingData
}) => {
  const { darkMode } = useDarkMode();
  const [currentStep, setCurrentStep] = useState(0);
  const [reflectionData, setReflectionData] = useState<ReflectionData | null>(existingData?.reflections || null);
  const [talkingPointsData, setTalkingPointsData] = useState<TalkingPointsData | null>(existingData?.talkingPoints || null);
  const [aiGeneratedPoints, setAiGeneratedPoints] = useState<Record<string, TalkingPointsResponse>>(existingData?.aiTalkingPoints || {});
  
  const bgColor = darkMode ? 'bg-gray-900' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  
  const steps = [
    { id: 'evidence', label: 'Evidence Review', icon: '📊' },
    { id: 'reflection', label: 'Reflection', icon: '💭' },
    { id: 'talking-points', label: 'Talking Points', icon: '💬' }
  ];
  
  const handleEvidenceNext = () => {
    setCurrentStep(1);
  };
  
  const handleAITalkingPointsGenerated = (rubricItemId: string, response: TalkingPointsResponse) => {
    // Store the full response object to handle both guidance and talking points
    setAiGeneratedPoints(prev => ({
      ...prev,
      [rubricItemId]: response
    }));
  };
  
  const handleReflectionNext = (data: ReflectionData) => {
    setReflectionData(data);
    setCurrentStep(2);
  };
  
  const handleReflectionBack = () => {
    setCurrentStep(0);
  };
  
  const handleTalkingPointsNext = (data: TalkingPointsData) => {
    setTalkingPointsData(data);
    if (reflectionData) {
      onComplete({ 
        reflections: reflectionData, 
        talkingPoints: data,
        aiTalkingPoints: aiGeneratedPoints 
      });
    }
  };
  
  const handleTalkingPointsBack = () => {
    setCurrentStep(1);
  };
  
  return (
    <Dialog open={true} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className={`mx-auto max-w-4xl w-full ${bgColor} rounded-xl shadow-2xl`}>
            {/* Header */}
            <div className={`flex items-center justify-between p-6 border-b ${borderColor}`}>
              <DialogTitle className={`text-xl font-semibold ${textColor}`}>
                Prepare for Your Final Review
              </DialogTitle>
              <button
                onClick={onClose}
                className={`p-2 rounded-lg transition-colors ${
                  darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
                }`}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            
            {/* Progress Steps */}
            <div className={`px-6 py-4 border-b ${borderColor}`}>
              <div className="flex items-center justify-between max-w-2xl mx-auto">
                {steps.map((step, index) => (
                  <div key={step.id} className="flex items-center">
                    <div
                      className={`flex items-center gap-3 ${
                        index === currentStep ? textColor : mutedTextColor
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                          index < currentStep
                            ? 'bg-green-500 text-white'
                            : index === currentStep
                            ? 'bg-blue-600 text-white'
                            : darkMode
                            ? 'bg-gray-700'
                            : 'bg-gray-200'
                        }`}
                      >
                        {index < currentStep ? '✓' : step.icon}
                      </div>
                      <span className={`text-sm font-medium hidden sm:block`}>
                        {step.label}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`w-12 sm:w-24 h-0.5 mx-2 ${
                          index < currentStep
                            ? 'bg-green-500'
                            : darkMode
                            ? 'bg-gray-700'
                            : 'bg-gray-300'
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <AnimatePresence mode="wait">
                {currentStep === 0 && (
                  <motion.div
                    key="evidence"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <EvidenceReviewStep
                      metrics={metrics}
                      focus={focus}
                      onNext={handleEvidenceNext}
                      onAITalkingPointsGenerated={handleAITalkingPointsGenerated}
                    />
                  </motion.div>
                )}
                
                {currentStep === 1 && (
                  <motion.div
                    key="reflection"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <ReflectionStep
                      onNext={handleReflectionNext}
                      onBack={handleReflectionBack}
                      initialData={reflectionData}
                    />
                  </motion.div>
                )}
                
                {currentStep === 2 && reflectionData && (
                  <motion.div
                    key="talking-points"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <TalkingPointsStep
                      metrics={metrics}
                      reflections={reflectionData}
                      aiGeneratedPoints={aiGeneratedPoints}
                      onNext={handleTalkingPointsNext}
                      onBack={handleTalkingPointsBack}
                      initialData={talkingPointsData}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default PreparationWizard;